import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { CalendarClock, Trash2 } from "lucide-react";

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(local) {
  if (!local) return null;
  // Interpretar em fuso horário local; devolve ISO UTC
  const d = new Date(local);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

export function InterviewScheduleDialog({ application, onClose, onSave, saving }) {
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [changeStatus, setChangeStatus] = useState(true);

  useEffect(() => {
    if (!application) {
      setDate("");
      setNotes("");
      setChangeStatus(true);
      return;
    }
    setDate(toLocalInput(application.interview_scheduled_at));
    setNotes(application.interview_notes || "");
    // Se já está em entrevista, não sugerir mudar de novo
    setChangeStatus(application.status !== "entrevista");
  }, [application]);

  const now = new Date();
  const minLocal = toLocalInput(now.toISOString());

  const handleSave = () => {
    if (!application) return;
    const iso = fromLocalInput(date);
    if (!iso) {
      onSave({ error: "Data inválida" });
      return;
    }
    const payload = {
      interview_scheduled_at: iso,
      interview_notes: notes.trim(),
    };
    if (changeStatus && application.status !== "entrevista") {
      payload.status = "entrevista";
    }
    onSave(payload);
  };

  const handleClear = () => {
    if (!application) return;
    onSave({ interview_scheduled_at: "", interview_notes: "" });
  };

  return (
    <Dialog open={!!application} onOpenChange={(o) => !o && !saving && onClose && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-blue-500" />
            {application?.interview_scheduled_at ? "Reagendar entrevista" : "Agendar entrevista"}
          </DialogTitle>
          {application && (
            <p className="text-sm text-slate-500">
              <span className="font-semibold text-slate-700">{application.candidate_name}</span> · {application.job_title}
            </p>
          )}
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <Label className="mb-1.5 block">Data e hora</Label>
            <Input
              data-testid="interview-date-input"
              type="datetime-local"
              value={date}
              min={minLocal}
              onChange={(e) => setDate(e.target.value)}
            />
            <p className="text-xs text-slate-500 mt-1.5">Hora local do candidato. Aparece no dashboard dele.</p>
          </div>

          <div>
            <Label className="mb-1.5 block">Notas (opcional)</Label>
            <Textarea
              data-testid="interview-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="Morada, o que trazer, link Zoom, contactos…"
              maxLength={2000}
            />
            <p className="text-xs text-slate-500 mt-1 text-right">{notes.length}/2000</p>
          </div>

          {application?.status !== "entrevista" && (
            <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                data-testid="interview-change-status"
                checked={changeStatus}
                onChange={(e) => setChangeStatus(e.target.checked)}
                className="mt-0.5"
              />
              <span>Marcar candidatura como <b>&quot;Entrevista&quot;</b> automaticamente</span>
            </label>
          )}
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2 mt-2">
          {application?.interview_scheduled_at && (
            <Button
              data-testid="interview-clear-btn"
              variant="ghost"
              onClick={handleClear}
              disabled={saving}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 sm:mr-auto">
              <Trash2 className="w-4 h-4 mr-1.5" /> Cancelar entrevista
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={saving}>Fechar</Button>
          <Button
            data-testid="interview-save-btn"
            onClick={handleSave}
            disabled={saving || !date}
            className="rounded-full bg-slate-900 hover:bg-slate-800">
            {saving ? "A guardar…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
